import { useEffect, useState } from 'react';
import { analyticsAPI } from '../services/api';
import { StatCard, Card, Spinner } from '../components/UI';
import { IconUsers, IconContent, IconAnnouncements, IconAnalytics, IconPosts, IconBooks, IconVideo, IconAudio } from '../components/Icons';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6','#ec4899','#8b5cf6','#14b8a6'];

function MiniList({ title, rows, subKey }) {
  if (!rows.length) return null;
  const max = rows[0]?.view_count || 1;
  return (
    <Card>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600 }}>
        {title}
      </div>
      {rows.map((item, i) => (
        <div key={item.id} style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '12px 20px', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
        }}>
          <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', width: 16 }}>
            {String(i + 1).padStart(2, '0')}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.title?.length > 50 ? item.title.slice(0, 50) + '…' : (item.title || '—')}
            </div>
            {item[subKey] && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{item[subKey]}</div>}
          </div>
          <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--accent)', fontWeight: 500 }}>
            {item.view_count} views
          </div>
          <div style={{ width: 80, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3, background: 'var(--accent)',
              width: `${Math.round((item.view_count / max) * 100)}%`,
            }}/>
          </div>
        </div>
      ))}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview]     = useState(null);
  const [daily, setDaily]           = useState([]);
  const [topContent, setTopContent] = useState([]);
  const [topPosts, setTopPosts]     = useState([]);
  const [topBooks, setTopBooks]     = useState([]);
  const [topVideos, setTopVideos]   = useState([]);
  const [topAudio, setTopAudio]     = useState([]);
  const [roles, setRoles]           = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    Promise.all([
      analyticsAPI.overview(),
      analyticsAPI.dailyViews(),
      analyticsAPI.topContent(5),
      analyticsAPI.topPosts(5),
      analyticsAPI.topBooks(5),
      analyticsAPI.topVideos(5),
      analyticsAPI.topAudio(5),
      analyticsAPI.roleBreakdown(),
    ]).then(([o, d, t, tp, tb, tv, ta, r]) => {
      setOverview(o.data.data);
      setDaily(d.data.data.map(x => ({ ...x, date: x.date?.slice(5) })));
      setTopContent(t.data.data);
      setTopPosts(tp.data.data);
      setTopBooks(tb.data.data);
      setTopVideos(tv.data.data);
      setTopAudio(ta.data.data);
      setRoles(r.data.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <Spinner size={32}/>
    </div>
  );

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>
          {greeting}, {user?.name?.split(' ')[0]}
        </h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>
          Here's what's happening across the foundation today.
        </p>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard icon={<IconUsers size={22}/>}         label="Total users"        value={overview?.users?.total}         sub={`${overview?.users?.active} active · ${overview?.users?.pending} pending`} color="var(--accent)"/>
        <StatCard icon={<IconContent size={22}/>}       label="Published files"    value={overview?.content?.total}       color="var(--blue)"/>
        <StatCard icon={<IconAnnouncements size={22}/>} label="Announcements sent" value={overview?.announcements?.total} color="var(--gold)"/>
        <StatCard icon={<IconAnalytics size={22}/>}     label="Views today"        value={overview?.views?.today}         sub={`${overview?.views?.last_7_days} this week`} color="#7c3aed"/>
        <StatCard icon={<IconPosts size={22}/>}         label="Posts"              value={overview?.posts?.total}         sub={`${overview?.posts?.published} published`}   color="#10b981"/>
        <StatCard icon={<IconBooks size={22}/>}         label="Books"              value={overview?.books?.total}         sub={`${overview?.books?.published} published`}   color="#f59e0b"/>
        <StatCard icon={<IconVideo size={22}/>}         label="Videos"             value={overview?.videos?.total}        sub={`${overview?.videos?.published} published`}  color="#3b82f6"/>
        <StatCard icon={<IconAudio size={22}/>}         label="Audio tracks"       value={overview?.audio?.total}         sub={`${overview?.audio?.published} published`}   color="#8b5cf6"/>
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Daily views */}
        <Card style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>All views — last 30 days</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={daily}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false}/>
              <YAxis tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false} width={28}/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}/>
              <Line type="monotone" dataKey="views" stroke="var(--accent)" strokeWidth={2} dot={false} activeDot={{ r: 4 }}/>
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Role breakdown */}
        <Card style={{ padding: '20px 20px 12px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>Users by role</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={roles} barSize={28}>
              <XAxis dataKey="role" tick={{ fontSize: 11, fill: 'var(--text3)' }} tickLine={false} axisLine={false}/>
              <YAxis tick={{ fontSize: 10, fill: 'var(--text3)' }} tickLine={false} axisLine={false} width={28}/>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)' }}/>
              <Bar dataKey="user_count" radius={[4, 4, 0, 0]}>
                {roles.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Top lists — 3 columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <MiniList title="Most viewed content" rows={topContent} subKey="category"/>
        <MiniList title="Most viewed posts"   rows={topPosts}   subKey="category"/>
        <MiniList title="Most viewed books"   rows={topBooks}   subKey="published_date"/>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <MiniList title="Most viewed videos"  rows={topVideos}  subKey={null}/>
        <MiniList title="Most played audio"   rows={topAudio}   subKey="album"/>
      </div>
    </div>
  );
}
